<template>
  <AdminModal
    :is-open="open"
    :modal-title="`환불 처리`"
    :close="modalClose"
    :confirm="confirm"
    :confirm-button-title="`확인`"
    :loading="loading"
    :confirm-disabled="isRefundConfirmDisabled"
    name="paymentRefundModal"
  >
    <b-container>
      <p>{{payment.buyerName}}의 결제 내역 {{payment.paymentKey}}를 환불합니다.</p>
      <!-- 결제 금액 입력 -->
      <b-form-group label="결제 금액 (기 취소 금액 합산)">
        <b-form-input v-model="originAmount" type="number" disabled/>
      </b-form-group>

      <!-- 환불 금액 입력 -->
      <b-form-group>
        <label for="amount2" class="form-label">
          취소/환불금액
          <span class="form-check form-switch form-check-inline">
            <input class="form-check-input" type="checkbox" role="switch" id="check" v-model="checkAllAmount">
            <label class="form-check-label" for="check">전체환불</label>
          </span>
        </label>
        <b-form-input v-model="refundAmount" type="number" :max="originAmount"></b-form-input>
      </b-form-group>

      <!-- 잔여금액 표시 -->
      <b-form-group label="잔여금액">
        <b-form-input :value="remainAmount" disabled></b-form-input>
      </b-form-group>

      <b-form-textarea
        rows="3"
        max-rows="6"
        v-model="memo"
        no-resize
        placeholder="메모"
      />

      <template v-if="requiresRefundAccountFields">
        <hr class="my-3"/>
        <p class="fw-semibold mb-2">환불 입금 계좌 <span class="text-danger">(무통장 입금 건 필수)</span></p>
        <b-form-group label="환불 입금 은행">
          <b-form-select
            v-model="refundBankCode"
            :options="inicisRefundBankOptions"
          />
        </b-form-group>
        <b-form-group label="계좌번호">
          <b-form-input v-model="refundAcctNum" placeholder="환불 받을 계좌번호"/>
        </b-form-group>
        <b-form-group label="예금주명">
          <b-form-input v-model="refundAcctName" placeholder="예금주명"/>
        </b-form-group>
      </template>

      <p class="text-danger mt-2">
        * 환불 시 원복할 수 없습니다. 꼭 확인하고 환불해주세요.
      </p>
    </b-container>
  </AdminModal>
</template>

<script>
import AdminModal from "@/components/modals/AdminModal.vue";
import api from "@/api/api";
import { INICIS_REFUND_BANK_OPTIONS } from "@/constants/inicisRefundBanks";

export default {
  name: "PaymentRefundModal",
  components: {AdminModal},
  props: ["payment", "modalTitle", "open", "modalClose", "confirmButtonTitle", "reload"],
  data() {
    return  {
      loading: false,
      refundAmount: 0,
      checkAllAmount: true,
      originAmount: 0,
      memo: "",
      refundBankCode: "",
      refundAcctNum: "",
      refundAcctName: "",
      inicisRefundBankOptions: INICIS_REFUND_BANK_OPTIONS,
    }
  },
  mounted() {
    if (this.checkAllAmount) {
      this.refundAmount = ( this.payment.amount - this.payment.refundAmount)
    }

    this.originAmount = this.payment.amount - this.payment.refundAmount
  },
  watch: {
    checkAllAmount() {
      if (this.checkAllAmount) {
        this.refundAmount = (this.payment.amount - this.payment.refundAmount)
      }
    },
    refundAmount(){
      if (this.refundAmount === this.payment.amount - this.payment.refundAmount){
        this.checkAllAmount = true
      } else {
        this.checkAllAmount = false
      }
    },
  },
  computed: {
    remainAmount(){
      const paymentAmount = (this.payment.amount - this.payment.refundAmount) || 0
      const remainAmount = paymentAmount - this.refundAmount
      return remainAmount < 0 ? 0 : remainAmount
    },
    /** 이니시스 무통장(가상계좌) 결제 완료·부분환불 후 추가 환불 시 계좌 정보 필수 */
    requiresRefundAccountFields() {
      const p = this.payment
      if (!p) return false
      return (
        p.pgType === 'INICIS' &&
        p.paymentType === 'vbank' &&
        ['complete', 'partial_refund'].includes(p.status)
      )
    },
    selectedRefundBankName() {
      const code = String(this.refundBankCode || "").trim()
      if (!code) return ""
      const found = this.inicisRefundBankOptions.find((option) => option.value === code)
      return found?.text || ""
    },
    isRefundConfirmDisabled() {
      if (!this.requiresRefundAccountFields) return false
      const code = String(this.refundBankCode || '').trim()
      const num = String(this.refundAcctNum || '').trim()
      const name = String(this.refundAcctName || '').trim()
      const bankName = String(this.selectedRefundBankName || '').trim()
      return !code || !bankName || !num || !name
    },
  },
  methods: {
    confirm(){
      this.$store.dispatch('modal/openModal', {
        type: 'warning',
        title: '결제 취소',
        html: `${this.payment.buyerName}님의 결제금액 중 ${this.refundAmount.toLocaleString("kr-KR")}원을 환불 처리합니다.`,
        confirm: () => {
          if (this.requiresRefundAccountFields) {
            const code = String(this.refundBankCode || '').trim()
            const bankName = String(this.selectedRefundBankName || '').trim()
            const num = String(this.refundAcctNum || '').trim()
            const name = String(this.refundAcctName || '').trim()
            if (!code || !bankName || !num || !name) {
              alert('무통장(가상계좌) 환불에는 은행 코드/은행명, 계좌번호, 예금주명이 필요합니다.')
              return
            }
          }

          this.loading = true;
          const refundParams = {
            id: this.payment.id,
            amount: this.refundAmount,
            memo: this.memo,
          };
          if (this.requiresRefundAccountFields) {
            refundParams.refundBankCode = String(this.refundBankCode).trim()
            refundParams.refundBankName = String(this.selectedRefundBankName).trim()
            refundParams.refundAcctNum = String(this.refundAcctNum).trim()
            refundParams.refundAcctName = String(this.refundAcctName).trim()
          }

          const handleSuccess = () => {
            this.reload();
            this.modalClose();
            this.loading = false;
          };

          const handleError = (error) => {
            const message = error?.response?.data?.message || error.message || "알 수 없는 오류가 발생했습니다.";
            alert(message);
            this.loading = false;
          };

          const finalize = () => {
            this.loading = false;
          };

          if (this.payment.pgType === "INICIS") {
            api.payment.refundByInicis(refundParams, handleSuccess, handleError, finalize);
          } else {
            api.payment.refundByPayple(refundParams, handleSuccess, handleError, finalize);
          }
        }
      });
    }
  },
}
</script>

<style scoped>

</style>